import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Steve } from '../../components/Steve';
import { S, t } from '../../i18n/strings';
import { useFinePointer, usePrefersReducedMotion } from '../../lib/motion';
import { useSteve } from '../../state/context';
import { IDLE_MS, startBounce, step, WAKE_GRACE_MS, type Bounds } from './bounce';
import styles from './xp.module.css';

/**
 * Taille du poisson à l'écran. Steve mesure 300 × 210 ; `.bouncerScale` le
 * réduit à 0,6, et ces deux constantes doivent suivre — c'est sur elles que
 * `bounce.ts` calcule le rebond.
 */
const SPRITE_WIDTH = 180;
const SPRITE_HEIGHT = 126;

/** Durée d'affichage du « ✦ » après un rebond dans un coin. */
const SPARKLE_MS = 900;

/** Les gestes qui repoussent l'attente, et qui referment l'écran affiché. */
const WAKE_EVENTS = ['pointermove', 'pointerdown', 'keydown', 'scroll', 'wheel'] as const;

/**
 * L'écran de veille : Steve rebondit sur les quatre bords, façon logo de lecteur
 * DVD, et tout le monde attend le coin.
 *
 * Trois interdits **pour le déclenchement automatique**, tous dans le handoff :
 *  - jamais pendant la lecture — le visiteur écoute, il ne s'est pas absenté ;
 *  - jamais sur tactile — il n'y a pas de « souris à bouger » pour en sortir ;
 *  - jamais sous `prefers-reduced-motion` — c'est du mouvement plein écran non
 *    sollicité, exactement ce que le réglage demande d'éviter.
 *
 * Ces trois règles ne valent que pour l'attente de 40 secondes. **Affichage →
 * Écran de veille l'ouvre sur demande**, quoi qu'il arrive : un visiteur qui
 * clique a demandé, il n'a pas subi. Sous « mouvement réduit », il s'ouvre alors
 * sans le rebond — Steve reste au milieu, et le réglage est respecté.
 *
 * Le rendu ne passe pas par React : la position vit dans une `ref` et la boucle
 * écrit directement le `transform`. Seul le « ✦ » des coins remonte dans l'état.
 */
interface Props {
  /**
   * Jeton d'ouverture manuelle : il change à chaque fois que le menu le
   * demande. Un booléen ne suffirait pas — refermer puis rouvrir doit
   * fonctionner, et deux `true` de suite ne se distinguent pas.
   */
  readonly openToken: number;
}

export function XpScreensaver({ openToken }: Props): ReactNode {
  const { lang, playing } = useSteve();
  const finePointer = useFinePointer();
  const reducedMotion = usePrefersReducedMotion();

  const [active, setActive] = useState(false);
  const [sparkle, setSparkle] = useState(false);
  const bouncerRef = useRef<HTMLDivElement>(null);

  const allowed = finePointer && !reducedMotion;

  // ── Ouverture à la demande, depuis le menu Affichage ────────────────────
  useEffect(() => {
    if (openToken > 0) setActive(true);
  }, [openToken]);

  // ── Compteur d'inactivité ───────────────────────────────────────────────
  //
  // Ne s'arme que dans les conditions du handoff. L'ouverture manuelle, elle,
  // passe par l'effet ci-dessus et ne dépend de rien.
  useEffect(() => {
    if (!allowed || playing) return;

    let idle = 0;
    const arm = (): void => {
      window.clearTimeout(idle);
      idle = window.setTimeout(() => {
        setActive(true);
      }, IDLE_MS);
    };

    for (const name of WAKE_EVENTS) window.addEventListener(name, arm, { passive: true });
    arm();

    return () => {
      window.clearTimeout(idle);
      for (const name of WAKE_EVENTS) window.removeEventListener(name, arm);
    };
  }, [allowed, playing]);

  // ── Sortie ──────────────────────────────────────────────────────────────
  //
  // Séparée de l'armement, et **sans condition** : quel que soit le chemin par
  // lequel l'écran s'est ouvert, le premier geste le referme. Sans cette
  // séparation, un écran ouvert depuis le menu sur un écran tactile — où
  // l'armement ne tourne pas — restait affiché sans aucun moyen d'en sortir.
  useEffect(() => {
    if (!active) return;

    /*
     * Les écouteurs sont posés tout de suite, mais ils ne mordent qu'après le
     * délai de grâce : sinon le geste qui a **ouvert** l'écran le referme.
     */
    let armed = false;
    const grace = window.setTimeout(() => {
      armed = true;
    }, WAKE_GRACE_MS);

    const wake = (): void => {
      if (armed) setActive(false);
    };

    for (const name of WAKE_EVENTS) window.addEventListener(name, wake, { passive: true });
    return () => {
      window.clearTimeout(grace);
      for (const name of WAKE_EVENTS) window.removeEventListener(name, wake);
    };
  }, [active]);

  // ── Le rebond ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!active) return;
    const node = bouncerRef.current;
    if (!node) return;

    // Mouvement réduit : l'écran s'affiche, mais Steve ne rebondit pas. On le
    // pose au milieu une fois pour toutes.
    if (reducedMotion) {
      const x = Math.max(0, (window.innerWidth - SPRITE_WIDTH) / 2);
      const y = Math.max(0, (window.innerHeight - SPRITE_HEIGHT) / 2);
      node.style.transform = `translate3d(${String(Math.round(x))}px, ${String(Math.round(y))}px, 0)`;
      return;
    }

    const bounds = (): Bounds => ({
      width: window.innerWidth,
      height: window.innerHeight,
      spriteWidth: SPRITE_WIDTH,
      spriteHeight: SPRITE_HEIGHT,
    });

    let state = startBounce(bounds());
    let last = performance.now();
    let frame = 0;
    let sparkleUntil = 0;

    const draw = (now: number): void => {
      const dt = (now - last) / 1000;
      last = now;
      const result = step(state, bounds(), dt);
      state = result.next;
      node.style.transform = `translate3d(${String(Math.round(state.x))}px, ${String(Math.round(state.y))}px, 0)`;

      if (result.corner) {
        sparkleUntil = now + SPARKLE_MS;
        setSparkle(true);
      } else if (sparkleUntil !== 0 && now > sparkleUntil) {
        sparkleUntil = 0;
        setSparkle(false);
      }
      frame = window.requestAnimationFrame(draw);
    };
    frame = window.requestAnimationFrame(draw);

    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [active, reducedMotion]);

  if (!active) return null;

  return (
    <div className={styles.screensaver} aria-label={t(S.xp.screensaver.label, lang)}>
      <div className={styles.bouncer} ref={bouncerRef}>
        <div className={styles.bouncerScale}>
          <Steve bobDuration="3.2s" />
        </div>
        {sparkle && (
          <span className={styles.sparkle} aria-hidden="true">
            ✦
          </span>
        )}
      </div>
      <p className={styles.screensaverHint}>{t(S.xp.screensaver.hint, lang)}</p>
    </div>
  );
}
